import { makeAutoObservable, observable } from 'mobx'
import type Asset from 'src/new_arch/modules/projectView/models/asset'
import type { AssetRoundJSON } from 'src/new_arch/modules/projectView/models/assetRound'

export type RoundRevert = {
	assetID: number,
	deletedRound: AssetRoundJSON,
	previousRound: AssetRoundJSON
}

type BasePendingDelete = {
	id: string,
	projectID: number,
	primaryName?: string,
	timeoutID: ReturnType<typeof setTimeout>,
	isUndoable: boolean
}

export type PendingItemsDelete = BasePendingDelete & {
	kind: 'items',
	assetIDs: number[],
	isBulk: boolean
}

export type PendingRoundDelete = BasePendingDelete & {
	kind: 'round',
	asset: Asset,
	assetID: number,
	currentRoundID: number,
	previousRoundID: number
}

export type PendingLastRoundDelete = BasePendingDelete & {
	kind: 'lastRound',
	assets: { id: number }[],
	reverts: RoundRevert[]
}

export type PendingDelete = PendingItemsDelete | PendingRoundDelete | PendingLastRoundDelete

export default class PendingDeletesStore {
	pendingDeletes: PendingDelete[] = []

	constructor() {
		makeAutoObservable( this, { pendingDeletes: observable.shallow } )
	}

	add( pendingDelete: PendingDelete ) {
		this.pendingDeletes.push( pendingDelete )
	}

	find( id: string ) {
		return this.pendingDeletes.find( pendingDelete => pendingDelete.id === id )
	}

	remove( id: string ) {
		this.pendingDeletes = this.pendingDeletes.filter( pendingDelete => pendingDelete.id !== id )
	}

	markAsNotUndoable( id: string ) {
		const pendingDelete = this.find( id )
		if ( pendingDelete ) pendingDelete.isUndoable = false
	}

	markAllAsNotUndoable() {
		this.pendingDeletes.forEach( ( pendingDelete ) => { pendingDelete.isUndoable = false } )
	}

	get mostRecentUndoable(): PendingDelete | undefined {
		for ( let index = this.pendingDeletes.length - 1; index >= 0; index -= 1 ) {
			if ( this.pendingDeletes[ index ].isUndoable ) return this.pendingDeletes[ index ]
		}

		return undefined
	}

	get hasUndoable(): boolean {
		return this.pendingDeletes.some( pendingDelete => pendingDelete.isUndoable )
	}

	get pendingDeletedRoundIDs(): Set<number> {
		const roundIDs = new Set<number>()
		this.pendingDeletes.forEach( ( pendingDelete ) => {
			if ( pendingDelete.kind === 'round' ) {
				roundIDs.add( pendingDelete.currentRoundID )
			} else if ( pendingDelete.kind === 'lastRound' ) {
				pendingDelete.reverts.forEach( revert => roundIDs.add( revert.deletedRound.id ) )
			}
		} )

		return roundIDs
	}

	get pendingDeletedAssetIDs(): Set<number> {
		const assetIDs = new Set<number>()
		this.pendingDeletes.forEach( ( pendingDelete ) => {
			if ( pendingDelete.kind === 'items' ) {
				pendingDelete.assetIDs.forEach( assetID => assetIDs.add( assetID ) )
			}
		} )

		return assetIDs
	}
}
